import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { Phone, Menu, X, Mail, MapPin, Clock } from "lucide-react";

// Pages
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import BookingPage from "@/pages/BookingPage";
import FinancingPage from "@/pages/FinancingPage";
import ReviewsPage from "@/pages/ReviewsPage";
import ContactPage from "@/pages/ContactPage";
import ProductsPage from "@/pages/ProductsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/products', label: 'Shop' },
    { path: '/financing', label: 'Financing' },
    { path: '/reviews', label: 'Reviews' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-nav' : 'bg-black/40 backdrop-blur-md'
      }`}
    >
      {/* Top neon banner across all pages */}
      <Link to="/" className="block w-full bg-black border-b border-white/10" data-testid="top-banner">
        <span className="mx-auto flex items-center justify-center h-10 md:h-12 w-56 md:w-80 overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_94a568bf-da54-41b2-bd7f-4fba373edc52/artifacts/fmqjld7k_8564C4CE-FB71-46C3-BA7F-F42659C7A335.png"
            alt="Virgil's Heating and Air"
            className="w-full h-full object-cover object-[center_42%]"
          />
        </span>
      </Link>
      <div className="container-custom">
        <div className="flex items-center justify-between h-24">
          {/* Logo - Made much larger */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_94a568bf-da54-41b2-bd7f-4fba373edc52/artifacts/03qpof76_613F63E1-88E2-4F0B-BD57-37714891EDA5.png"
              alt="Virgil's Heating and Air"
              className="h-16 md:h-20 w-auto rounded-full"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`nav-link text-base ${
                  location.pathname === link.path ? 'active' : ''
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Phone CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href="tel:7073843409" 
              data-testid="phone-cta"
              className="flex items-center gap-2 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-shadow"
            >
              <Phone className="w-5 h-5" />
              707-384-3409
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden glass-nav border-t border-white/10 py-6 px-4 shadow-xl rounded-b-2xl" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`block py-4 px-4 text-lg font-medium rounded-xl transition-colors ${
                  location.pathname === link.path 
                    ? 'text-white bg-[#1E6BFF]/15' 
                    : 'text-gray-300 hover:bg-white/5'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <a 
              href="tel:7073843409" 
              className="block mt-4 py-4 px-6 btn-urgent text-center font-bold text-lg rounded-full"
            >
              707-384-3409
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-gradient-to-b from-[#0a0a0a] to-[#050505] border-t border-white/10 text-white py-16" data-testid="footer">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Info - Larger logo */}
          <div className="lg:col-span-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_94a568bf-da54-41b2-bd7f-4fba373edc52/artifacts/03qpof76_613F63E1-88E2-4F0B-BD57-37714891EDA5.png"
              alt="Virgil's Heating and Air"
              className="h-32 w-auto mb-6 rounded-full shadow-lg"
            />
            <p className="text-gray-400 mb-4 max-w-md text-lg">
              Serving the Greater Sacramento Area and surrounding areas with professional HVAC services since 2010.
            </p>
            <p className="font-mono text-sm text-gray-500" data-testid="license-number">
              CLSB# 1054485
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-xl mb-6 text-white">Quick Links</h4>
            <ul className="space-y-4">
              <li><Link to="/services" className="footer-link text-lg hover:text-[#3B82F6]">Our Services</Link></li>
              <li><Link to="/booking" className="footer-link text-lg hover:text-[#3B82F6]">Book Appointment</Link></li>
              <li><Link to="/financing" className="footer-link text-lg hover:text-[#3B82F6]">Financing Options</Link></li>
              <li><Link to="/reviews" className="footer-link text-lg hover:text-[#3B82F6]">Customer Reviews</Link></li>
              <li>
                <a href="https://share.google/IrL6h8FY38NM6LQjA" target="_blank" rel="noopener noreferrer" className="footer-link text-lg hover:text-[#3B82F6] inline-flex items-center gap-2" data-testid="footer-google-link">
                  <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                    <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                  </svg>
                  Find Us on Google
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading font-bold text-xl mb-6 text-white">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a href="tel:7073843409" className="flex items-center gap-3 text-[#5b93ff] hover:text-[#8fb4ff] text-lg font-semibold transition-colors" data-testid="footer-phone">
                  <Phone className="w-5 h-5" />
                  707-384-3409
                </a>
              </li>
              <li>
                <a href="mailto:heyvirgil@virgilshvac.com" className="flex items-center gap-3 text-[#5b93ff] hover:text-[#8fb4ff] text-lg transition-colors" data-testid="footer-email">
                  <Mail className="w-5 h-5 shrink-0" />
                  heyvirgil@virgilshvac.com
                </a>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-lg">
                <MapPin className="w-5 h-5 shrink-0" />
                Greater Sacramento Area
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <Clock className="w-5 h-5 shrink-0 mt-1" />
                <span>
                  Mon-Fri: 8AM-6PM<br />
                  <span className="text-[#ff6a5c] font-medium">24/7 Emergency Service</span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Virgil's Heating and Air Conditioning. All rights reserved.
          </p>
          <a 
            href="tel:7073843409" 
            className="flex items-center gap-2 text-2xl font-heading font-bold neon-red-text transition-colors"
            data-testid="footer-phone-large"
          >
            <Phone className="w-6 h-6" />
            707-384-3409
          </a>
        </div>
      </div>
    </footer>
  );
};

function App() {
  useEffect(() => {
    // Seed initial reviews on app load
    const seedReviews = async () => {
      try {
        await axios.post(`${API}/seed-reviews`);
      } catch (e) {
        console.log('Reviews already seeded or error:', e.message);
      }
    };
    seedReviews();
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/financing" element={<FinancingPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/products" element={<ProductsPage />} />
        </Routes>
        <Footer />
        {/* Sticky mobile call button - converts on-the-go / emergency traffic */}
        <a
          href="tel:7073843409"
          data-testid="sticky-mobile-call"
          className="lg:hidden fixed bottom-20 left-5 z-[60] flex items-center gap-1 neon-call-btn px-2 py-1 text-[10px] animate-pulse-glow"
        >
          <Phone className="w-2.5 h-2.5" />
          Call Now
        </a>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
